import { useObserver } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { usePropertyAiGradeStore } from "..";
import IProperty from "../interfaces/IProperty";
import PropertyAiGradeApiService, { IPropertyAiGrade } from "../services/propertyAiGradeApiService";

export default function usePropertyAiGrade(property: IProperty | undefined): IPropertyAiGradeInfo {
    const aiGradeStore = usePropertyAiGradeStore();
    const aiGradeApiService = useMemo(() => new PropertyAiGradeApiService(), []);
    const [isFetchCalled, setIsFetchCalled] = useState(false);

    useEffect(() => setIsFetchCalled(false), [property]);

    const fetchAiGrade = useCallback(() => {
        if (!property || aiGradeStore.getIsLoadingFor(property.id) || aiGradeStore.getAiGradeFor(property.id))
            return;

        aiGradeStore.setIsErrorFor(property.id, false);
        aiGradeStore.setIsLoadingFor(property.id, true);
        setIsFetchCalled(true);
    }, [aiGradeStore, property]);

    const fetchInternal = async () => {
        if (!property) return;

        if (aiGradeStore.getAiGradeFor(property.id) || aiGradeStore.getIsErrorFor(property.id)) {
            aiGradeStore.stopLoading(property.id);
            return;
        }

        try {
            const aiGrade = await aiGradeApiService.fetchAiGrade(property);
            aiGradeStore.setAiGradeFor(property.id, aiGrade);
        } catch (error) {
            toast.error(`Failed to compute AI grade: ${error}`);
            aiGradeStore.setIsErrorFor(property.id, true);
        } finally {
            aiGradeStore.stopLoading(property.id);
        }
    };

    useEffect(() => {
        if (property && isFetchCalled) {
            setIsFetchCalled(false);
            fetchInternal();
        }
    }, [property, isFetchCalled]);

    return useObserver(() => ({
        aiGrade: property ? aiGradeStore.getAiGradeFor(property.id) : undefined,
        isLoading: aiGradeStore.getIsLoadingFor(property?.id),
        isError: aiGradeStore.getIsErrorFor(property?.id),
        fetchAiGrade,
    }));
}

interface IPropertyAiGradeInfo {
    aiGrade?: IPropertyAiGrade;
    isLoading: boolean;
    isError: boolean;
    fetchAiGrade: () => void;
}
